'use client'

import { useEffect, useState, useCallback } from 'react'
import type { DailyUsage } from '@/lib/billing/types'
import { formatCredits } from '@/lib/billing/utils/credits'
import { useAuth } from '@/hooks/use-auth'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Loader2, BarChart3 } from 'lucide-react'
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts'

/** Text split into the agents' own requests and Conversation Quality's, stacked in one bar. */
interface ChartDay extends DailyUsage {
  agent_count: number
  agent_cost: number
}

export function UsageChart() {
  const { fetchWithAuth } = useAuth()
  const [data, setData] = useState<ChartDay[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(false)
      const res = await fetchWithAuth('/api/billing/usage-daily?days=30')
      if (!res.ok) { setError(true); return }
      const json = (await res.json()) as { days?: DailyUsage[] }
      setData(
        (json.days ?? []).map((day) => ({
          ...day,
          agent_count: day.text_count - day.quality_count,
          agent_cost: day.text_cost - day.quality_cost,
        })),
      )
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [fetchWithAuth])

  useEffect(() => { fetchData() }, [fetchData])

  const hasData = data.some((d) => d.text_count > 0 || d.voice_count > 0)
  // Only charges the tenant paid: a platform-paid quality call leaves none, so this stays off for it.
  const hasQuality = data.some((d) => d.quality_count > 0)

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-1">
          <BarChart3 className="h-4 w-4" /> <span>Usage (Last 30 Days)</span>
        </CardTitle>
        <CardDescription>
          Daily text and voice usage breakdown
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : null}
        {!loading && error ? <p className="text-sm text-muted-foreground py-4 text-center">Unable to load usage data</p> : null}
        {!loading && !error && !hasData ? <p className="text-sm text-muted-foreground py-4 text-center">No usage recorded yet</p> : null}
        {!loading && !error && hasData ? (
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} barGap={0} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatShortDate}
                  tick={{ fontSize: 11 }}
                  interval="preserveStartEnd"
                  className="text-muted-foreground"
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  className="text-muted-foreground"
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  wrapperStyle={{ fontSize: 12 }}
                  iconType="circle"
                  iconSize={8}
                />
                {hasQuality ? (
                  <>
                    <Bar dataKey="agent_count" name="Text Requests" stackId="text" fill="#3b82f6" />
                    <Bar
                      dataKey="quality_count"
                      name="Conversation Quality"
                      stackId="text"
                      fill="#c4b5fd"
                      stroke="hsl(var(--background))"
                      strokeWidth={1}
                      radius={[2, 2, 0, 0]}
                    />
                  </>
                ) : (
                  <Bar dataKey="text_count" name="Text Requests" fill="#3b82f6" radius={[2, 2, 0, 0]} />
                )}
                <Bar dataKey="voice_count" name="Voice Calls" fill="#10b981" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function CustomTooltip({ active, payload, label }: {
  active?: boolean
  payload?: { payload?: ChartDay }[]
  label?: string | number
}) {
  if (!active || !payload?.length) return null
  const day = payload[0]?.payload
  if (!day) return null

  return (
    <div className="rounded-lg border bg-background p-3 shadow-md text-sm">
      <p className="font-medium mb-1">{formatShortDate(String(label))}</p>
      <div className="space-y-0.5 text-xs">
        <p className="text-blue-600">{`Text: ${day.agent_count} requests · ${formatCredits(day.agent_cost)}`}</p>
        {day.quality_count > 0 && (
          <p className="text-violet-600">
            {`Conversation Quality: ${day.quality_count} requests · ${formatCredits(day.quality_cost)}`}
          </p>
        )}
        <p className="text-emerald-600">{`Voice: ${day.voice_count} calls · ${formatCredits(day.voice_cost)}`}</p>
        <p className="text-muted-foreground font-medium mt-1">{`Total: ${formatCredits(day.text_cost + day.voice_cost)}`}</p>
      </div>
    </div>
  )
}
